import { digger } from '@cbre/digger';
import {
  concatValuesBy,
  defaultTxt,
  formatNumber,
  formatValueWithUom,
  MiqMoment,
  simpleField,
  UomFormat,
} from '@miq/utils';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { DataObject, ReportRecord } from '../../../models/report.model';
import {
  DisplayFnArgumentsInfo,
  DisplayFunctionOptions,
  isDateDataType,
  isNumericDataType,
  isStringDataType,
  LovValues,
} from '../../../models/template.model';
import { TEMPLATE_LAYOUT } from '../template-blueprints.const';
import { getRegularOrColloquialVal } from '../template-lov-attributes.const';
export const COMMON_UOM_MAP = {
  // Prefix
  USD: 'US Dollar',
  // Suffix
  SF: 'sq ft',
  SMt: 'sqm',
  'SQ M': 'sqm',
  sqft: 'sq ft',
};

const EntityRecord = {
  LeaseComp: 'lease_comp',
};
const BLANK_SPACE = ' ';

export function entityRecordSwitch(
  record: ReportRecord,
  argumentsInfo: DisplayFnArgumentsInfo
): DataObject | DataObject[] {
  return argumentsInfo.entity === 'property' ? record.associatedProperty || record.data : record.data;
}

function safeNumericValArray(record: { [p: string]: any }, arg: DisplayFnArgumentsInfo) {
  const { path, field } = arg;
  let val = digger(record, path || field);
  if (Array.isArray(val)) {
    val = !!val.length ? val : null;
  } else {
    val = [val];
  }
  return val;
}

function handleNumericValue(
  entityRecord: { [p: string]: any },
  arg: DisplayFnArgumentsInfo,
  uom: boolean = true,
  uomLovs?: LovValues[],
  defaultText = defaultTxt
) {
  const { dataType } = arg;
  const hasUom = arg.uomArgument != null && uom;
  const formatConfig: Partial<UomFormat> = {
    digits: arg.dataFormat,
    location: arg.location ?? 'en-US',
  };
  const val = safeNumericValArray(entityRecord, arg);
  let uomVal = hasUom
    ? safeNumericValArray(entityRecord, arg.uomArgument)
    : Array(val != null ? val.length : 0).fill(undefined);
  const uomValues = getRegularOrColloquialVal(uomVal, uomLovs);
  uomVal = !!uomLovs ? (Array.isArray(uomValues) ? uomValues : [uomValues]) : uomVal;
  return (val || []).map((numericVal, index) => {
    formatConfig.value = numericVal;
    let result = numericVal;
    if (hasUom) {
      if (arg.uomArgument.isPrefix) {
        formatConfig.prefix = uomVal[index];
      } else {
        formatConfig.uom = uomVal[index];
      }
      result = numericVal != null ? formatValueWithUom(formatConfig, entityRecord) : defaultText;
    } else if (dataType !== 'id') {
      result = formatNumber(numericVal, undefined, arg.dataFormat, defaultText);
    }
    return result;
  });
}

function handleStringValue(entityRecord: { [p: string]: any }, arg: DisplayFnArgumentsInfo, defaultText) {
  return simpleField(entityRecord, arg.path || arg.field, defaultText);
}

function handleDateValue(
  entityRecord: { [p: string]: any },
  arg: DisplayFnArgumentsInfo,
  defaultText = defaultTxt
): string | string[] {
  const { dataType } = arg;
  const val: string | string[] = digger(entityRecord, arg.path || arg.field);
  const toUtc = dataType !== 'timestamp with time zone';
  let result: string | string[] = defaultText;
  if (val != null && (dataType === 'date' || dataType === 'timestamp with time zone')) {
    if (Array.isArray(val)) {
      result = val.map(v =>
        v != null ? MiqMoment.formatFromInputFormat(v, MiqMoment.utcInputFormat, arg.dataFormat, toUtc) : defaultText
      );
    } else {
      result = MiqMoment.formatFromInputFormat(val, MiqMoment.utcInputFormat, arg.dataFormat, toUtc);
    }
  } else if (dataType === 'month') {
    // TODO: implement month
    result = val;
  }
  return result;
}

function resolveBooleanDisplayValue(entityRecord: { [p: string]: any }, val: any): string {
  if (entityRecord?.entity.entity_type === EntityRecord.LeaseComp) {
    return val ? 'Assumed' : 'Actual';
  }
  return val ? 'Yes' : 'No';
}

function handleBooleanValue(entityRecord: { [p: string]: any }, arg: DisplayFnArgumentsInfo): string | string[] {
  const val = digger(entityRecord, arg.path || arg.field);
  let result: string | string[] = defaultTxt;
  if (val != null) {
    result = Array.isArray(val)
      ? val.map(v => (v != null ? (v ? 'Yes' : 'No') : defaultTxt))
      : resolveBooleanDisplayValue(entityRecord, val);
  }
  return result;
}

export function mapAndFormatDynamicValue(
  reportRecord: ReportRecord,
  arg: DisplayFnArgumentsInfo,
  uomLov?: LovValues[],
  uom?: boolean,
  pickFirst = true,
  defaultText = defaultTxt
): string | string[] {
  if (arg == null || arg.field == null) {
    return null;
  }
  const entityRecord = (arg.isRecordTableData ? reportRecord : entityRecordSwitch(reportRecord, arg)) as ReportRecord;

  if (Array.isArray(entityRecord)) {
    if (pickFirst) {
      return getValue(arg, entityRecord[0], uom, uomLov, defaultText);
    } else {
      return entityRecord.map(rec => getValue(arg, rec, uom, uomLov, defaultText));
    }
  } else {
    return getValue(arg, entityRecord, uom, uomLov, defaultText);
  }
}

function getValue(
  arg: DisplayFnArgumentsInfo,
  record: ReportRecord,
  uom: boolean,
  uomLov: LovValues[],
  defaultText
): string {
  const { dataType } = arg;
  let result;
  if (isNumericDataType(dataType)) {
    result = handleNumericValue(record, arg, uom, uomLov, defaultText);
  } else if (isStringDataType(dataType)) {
    result = handleStringValue(record, arg, defaultText);
  } else if (isDateDataType(dataType)) {
    result = handleDateValue(record, arg, defaultText);
  } else {
    result = handleBooleanValue(record, arg);
  }
  return result;
}

export function formatExternalMeasurementFieldVal(
  reportRecord: ReportRecord,
  arg: DisplayFnArgumentsInfo,
  fieldValueType: string
) {
  const entityRecord = entityRecordSwitch(reportRecord, arg);
  const record = Array.isArray(entityRecord) ? entityRecord[0] : entityRecord;
  const data = digger(record, arg.field.split('/')[0]);
  const result = (data || []).find(args => args[arg.field.split('/')[1]] === arg.dataFormattingDesc);
  return result && result[fieldValueType] ? result[fieldValueType] : '-';
}

export function minMaxRangeFormattedValue(
  record: ReportRecord,
  minVal: DisplayFnArgumentsInfo,
  maxVal: DisplayFnArgumentsInfo
): string | string[] {
  const minFormattedValue: string | string[] = mapAndFormatDynamicValue(record, minVal);
  const maxFormattedValue: string | string[] = mapAndFormatDynamicValue(record, maxVal);
  const minValue = concatValuesBy('', ...(Array.isArray(minFormattedValue) ? minFormattedValue : [minFormattedValue]));
  const maxValue = concatValuesBy('', ...(Array.isArray(maxFormattedValue) ? maxFormattedValue : [maxFormattedValue]));
  return minValue === maxValue ? minValue : concatValuesBy(' - ', minValue, maxValue);
}

export function displayFnWrapper(record: ReportRecord, _, arg: DisplayFnArgumentsInfo) {
  return mapAndFormatDynamicValue(record, arg);
}

export function displayFnArrayWrapper(record: ReportRecord, _, _arg: DisplayFnArgumentsInfo) {
  const result = <string[]>mapAndFormatDynamicValue(record, _arg, null, null, false, '');
  return Array.isArray(result) ? result.join('\n') : BLANK_SPACE;
}
/**
 * Adding hyphen where there is empty values or null
 */
export function addingBlank(data: string[]) {
  const result = data.map(entity => {
    return entity === null || entity === '' ? '-' : entity;
  });
  return result;
}
/**
 * Formats Arrays values extracting from records and skipping duplicates
 */
export function displayFnArrayWraperUnique(record: ReportRecord, _, arg: DisplayFnArgumentsInfo) {
  const result = mapAndFormatDynamicValue(record, arg, null, null, false, BLANK_SPACE) as string[];
  if (Array.isArray(result)) {
    const flatted = result.map(r => (Array.isArray(r) && r.length ? r[0] : r));
    const uniqueValues = [...new Set(flatted)];
    return uniqueValues.join('\n');
  } else {
    return BLANK_SPACE;
  }
}

export function displayFnArrayWraperUnique2(record: ReportRecord, _, _arg: DisplayFnArgumentsInfo) {
  const result = <string[]>mapAndFormatDynamicValue(record, _arg, null, null, false, BLANK_SPACE);
  if (Array.isArray(result)) {
    let flattedResult = result.reduce((acc, obj) => [...acc, ...obj], []);
    flattedResult = [...new Set(result)];
    return result.join('\n');
  } else {
    return BLANK_SPACE;
  }
}
export function getLovAttributesFormattedData(
  record: ReportRecord,
  attributeInfo: DisplayFnArgumentsInfo
): Observable<string> {
  return attributeInfo.lovValues.pipe(
    map((lov: LovValues[]) => {
      const waterVal = mapAndFormatDynamicValue(record, attributeInfo) as string[];
      const result: string | string[] = getRegularOrColloquialVal(waterVal, lov);
      return Array.isArray(result) ? result.join() : result;
    })
  );
}

export function getMeasuremetsAttributesData(
  record: ReportRecord,
  attributeInfo: DisplayFnArgumentsInfo,
  lovData: Observable<LovValues[]>
) {
  return lovData.pipe(
    map((childLov: LovValues[]) => {
      const greenRating = formatExternalMeasurementFieldVal(
        record,
        attributeInfo,
        'green_building_cert_level_desc'
      ).toUpperCase();
      const result: string | string[] = getRegularOrColloquialVal(greenRating, childLov);
      return Array.isArray(result) ? result.join() : result;
    })
  );
}

export function formatQuotingPrice(
  quotingPriceVal: string | string[],
  rentPerSqftVal: string | string[],
  totalGrossAreaUom: string
) {
  const formatRentPerSqftVal =
    rentPerSqftVal && !isDash(rentPerSqftVal) ? `${rentPerSqftVal} ${totalGrossAreaUom}` : '-';
  let result: string | string[] = Dash;
  const checkQuotingPriceVal = quotingPriceVal && !isDash(quotingPriceVal);
  const checkformatRentPerSqftVal = formatRentPerSqftVal && !isDash(formatRentPerSqftVal);
  if (checkQuotingPriceVal && checkformatRentPerSqftVal) {
    result = `${quotingPriceVal} (${formatRentPerSqftVal})`;
  } else if (checkQuotingPriceVal || checkformatRentPerSqftVal) {
    result = checkQuotingPriceVal ? quotingPriceVal : formatRentPerSqftVal;
  }
  return result;
}

/**
 * Formats display values already containing UOM appended after value with new mapping
 * @param value Holds single string value that contains original UOM appended
 * @param symbols Mapping of symbols to replace original UOM display
 * @param isPrefix Determines positioning of reformatted UOM; if true, it will position in front
 */
export const formatByUom = (value: string, symbols: DataObject = null, isPrefix = false): string => {
  if (value == null || (isPrefix && !symbols)) {
    return value;
  }
  const numericValue = value.split(' ')[0];
  const uom = value.split(' ').slice(1).join(' ');
  let formattedValue: string;
  if (isPrefix) {
    formattedValue = !!COMMON_UOM_MAP[uom]
      ? `${symbols[COMMON_UOM_MAP[uom]]}${numericValue}`
      : `${symbols[uom] || ''}${numericValue}`;
  } else {
    formattedValue = !!COMMON_UOM_MAP[uom] ? `${numericValue} ${COMMON_UOM_MAP[uom]}` : value;
  }
  return formattedValue;
};

export const Dash = '-';
export function isDash(val: string | string[]): boolean {
  val = Array.isArray(val) ? val.join() : val;
  return typeof val === 'string' ? val.trim() === Dash : val === Dash;
}

export function formatYesOrNoValues(val: string, options: DisplayFunctionOptions): string {
  let result = '-';
  if (val != null && !isDash(val)) {
    result = val.toLowerCase() === 'yes' ? options.jsonData.recordPPT.y : options.jsonData.recordPPT.n;
  }
  return result;
}

export function getUsageSubType(record: ReportRecord, attributeInfo: DisplayFnArgumentsInfo, lovData: LovValues[]) {
  const fieldValue = digger(record.associatedProperty, attributeInfo.field);
  const returnValue = (fieldValue || []).map(value => {
    const found = lovData.find(lov => lov.key === value);
    return found ? found.value : value;
  });
  return returnValue && returnValue.length ? concatValuesBy(', ', ...returnValue) : fieldValue;
}

export function getStatus(record: ReportRecord, attributeInfo: DisplayFnArgumentsInfo, lovData: LovValues[]) {
  const fieldValue = digger(record.data, attributeInfo.field);
  const foundValue = lovData.find(lov => lov.key === fieldValue);
  return foundValue && foundValue.value ? foundValue.value : null;
}

export function getWidthAndHeightForMap(templateId: number) {
  return TEMPLATE_LAYOUT.PORTRAIT && TEMPLATE_LAYOUT.PORTRAIT.includes(templateId)
    ? { width: 700, height: 840 }
    : { width: 1015, height: 563 };
}
